import React, { JSX } from "react";
import { type Price as Offer } from "../pricing";
import classNames from "classnames";
import { PriceFormat } from "@/cms/sanity.types";
import {
  Typography,
  TypographyVariant,
} from "@cappelendamm/design-system/react";
import { formatPrice, PriceTypes } from "@cappelendamm/e-commerce-services";

export type PriceProps = Partial<Offer> & {
  format?: Omit<PriceFormat, "_type"> | null;
  type?: PriceTypes;
  variant?: TypographyVariant;
  subjectToVAT?: boolean | null;
  totalWithVAT?: number | null;
  isPending?: boolean;
};
export function Price({
  current,
  previous,
  type = "single",
  format,
  className,
  hasOffer: _hasOffer,
  variant = "title-lg",
  subjectToVAT,
  totalWithVAT,
  isPending,
  ...rest
}: PriceProps & JSX.IntrinsicElements["div"]) {
  if (!previous && format?.title) {
    const title = format.title.replace("{price}", formatPrice(current));
    const description = format.description?.replace(
      "{price}",
      formatPrice(current),
    );
    return (
      <Typography
        {...rest}
        className={className}
        as="div"
        variant={variant}
        aria-label={`${title} ${description}`}
      >
        {title}
        {description && (
          <Typography as="div" variant="body-base">
            {description}
          </Typography>
        )}
      </Typography>
    );
  }

  const currentPriceElement = (
    <Typography
      as="span"
      variant={variant}
      className={classNames(
        _hasOffer ? "text-red-500" : "text-gray-800",
        "font-bold",
      )}
    >
      {formatPrice(current, type)}
    </Typography>
  );

  if (subjectToVAT) {
    return (
      <div {...rest} className={classNames(className, "flex flex-col gap-2")}>
        <div className="flex items-center gap-2">
          {_hasOffer && (
            <Typography
              as="span"
              className="order-last text-sm font-normal text-gray-800"
            >
              {`Før ${formatPrice(previous, type)}`}
            </Typography>
          )}
          <Typography
            as="span"
            variant={variant}
            className={classNames(
              _hasOffer ? "text-red-500" : "text-gray-800",
              "font-bold",
            )}
          >
            {formatPrice(totalWithVAT, type)}
          </Typography>
        </div>
        <Typography
          as="span"
          className="order-last text-sm font-normal text-gray-800"
        >
          {`${formatPrice(current, type)} ekskl. mva`}
        </Typography>
      </div>
    );
  }

  // Avoid showing a discount label while updating the cart, as the price will
  // be different until the update is complete.
  if (isPending) {
    return (
      <Typography as="div" variant={variant} {...rest} className={className}>
        {formatPrice(current, type)}
      </Typography>
    );
  }

  if (!_hasOffer) {
    return (
      <Typography as="div" variant={variant} {...rest} className={className}>
        {formatPrice(current, type)}
      </Typography>
    );
  }

  return (
    <div {...rest} className={classNames(className, "flex items-center gap-2")}>
      {currentPriceElement}
      <Typography
        as="span"
        className="order-last text-sm font-normal text-gray-800"
      >
        {`Før ${formatPrice(previous, type)}`}
      </Typography>
    </div>
  );
}
